import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Link,
    Stack,
    Tooltip,
    Snackbar,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ListItemIcon,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CopyrightIcon from '@mui/icons-material/Copyright';
import DomainIcon from '@mui/icons-material/Domain';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import { Lead, Note, Business, UserSettableLeadStatus } from '../../../models/Lead';
import { updateLead, getBusinessForLead } from '../../../services/leadService';
import { ManualOutreachForm } from './ManualOutreachForm';

interface LeadDetailsComponentProps {
    lead: Lead;
    onLeadUpdate: (updatedLead: Lead) => void;
}

const USER_SETTABLE_STATUSES: UserSettableLeadStatus[] = ['In Review', 'Approved', 'Rejected'];

const getStatusColor = (status: string): "default" | "primary" | "success" | "info" | "error" | "warning" => {
    switch (status) {
        case 'new':
            return 'info';
        case 'In Review':
            return 'primary';
        case 'Approved':
            return 'success';
        case 'Rejected':
            return 'error';
        case 'In Progress':
            return 'warning';
        default:
            return 'default';
    }
};

export const LeadDetailsComponent: React.FC<LeadDetailsComponentProps> = ({ lead, onLeadUpdate }) => {
    const [newNote, setNewNote] = useState('');
    const [notes, setNotes] = useState<Note[]>(() => {
        // Initialize notes as an empty array if undefined or not an array
        if (!lead.notes || !Array.isArray(lead.notes)) {
            return [];
        }
        // Convert string dates to Date objects if needed
        return lead.notes.map(note => ({
            ...note,
            createdAt: note.createdAt instanceof Date ? note.createdAt : new Date(note.createdAt)
        }));
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCopySuccess, setShowCopySuccess] = useState(false);
    const [business, setBusiness] = useState<Business | null>(null);
    const [isLoadingBusiness, setIsLoadingBusiness] = useState(true);
    const [editingPhone, setEditingPhone] = useState(false);
    const [editingWebsite, setEditingWebsite] = useState(false);
    const [phoneValue, setPhoneValue] = useState(lead.phone || '');
    const [websiteValue, setWebsiteValue] = useState(lead.website || '');
    const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingNoteText, setEditingNoteText] = useState('');
    const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
    const [editingLocation, setEditingLocation] = useState(false);
    const [cityValue, setCityValue] = useState(lead.city || '');
    const [stateValue, setStateValue] = useState(lead.state || '');

    const isLockedStatus = lead.status === 'In Progress' || lead.status === 'Complete';

    useEffect(() => {
        const fetchBusiness = async () => {
            setIsLoadingBusiness(true);
            try {
                const businessData = await getBusinessForLead(lead.businessId);
                setBusiness(businessData);

                // Add note if no website was found
                if (!businessData?.website && !lead.website) {
                    const noWebsiteNote: Note = {
                        id: Date.now().toString(),
                        text: `No website was found during business analysis.`,
                        createdAt: new Date(),
                    };
                    handleAddSystemNote(noWebsiteNote);
                }
            } catch (error) {
                console.error('Error fetching business data:', error);
            } finally {
                setIsLoadingBusiness(false);
            }
        };

        fetchBusiness();
    }, [lead.businessId]);

    const handleAddSystemNote = async (note: Note) => {
        try {
            const updatedNotes = [...notes, note];
            await updateLead(lead.id, { notes: updatedNotes });
            setNotes(updatedNotes);
            onLeadUpdate({ ...lead, notes: updatedNotes });
        } catch (error) {
            console.error('Error adding system note:', error);
        }
    };

    const handleUpdateField = async (field: 'phone' | 'website' | 'city' | 'state', value: string) => {
        try {
            setIsSubmitting(true);
            const oldValue = field === 'phone' ? lead.phone : field === 'website' ? lead.website : field === 'city' ? lead.city : lead.state;

            // Create note about the change
            const changeNote: Note = {
                id: Date.now().toString(),
                text: `${field.charAt(0).toUpperCase() + field.slice(1)} updated from "${oldValue || 'none'}" to "${value || 'none'}"`,
                createdAt: new Date(),
            };

            const updatedNotes = [...notes, changeNote];

            // Update lead with new value and note
            await updateLead(lead.id, {
                [field]: value,
                notes: updatedNotes
            });

            // Update local state
            setNotes(updatedNotes);
            onLeadUpdate({ ...lead, [field]: value, notes: updatedNotes });

            // Reset editing state
            if (field === 'phone') {
                setEditingPhone(false);
            } else if (field === 'website') {
                setEditingWebsite(false);
            } else if (field === 'city') {
                setEditingLocation(false);
            } else if (field === 'state') {
                setEditingLocation(false);
            }

            setShowUpdateSuccess(true);
        } catch (error) {
            console.error(`Error updating ${field}:`, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyBusinessId = async () => {
        try {
            await navigator.clipboard.writeText(lead.businessId || '');
            setShowCopySuccess(true);
        } catch (err) {
            console.error('Failed to copy business ID:', err);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;

        try {
            setIsSubmitting(true);
            const newNoteObj: Note = {
                id: Date.now().toString(),
                text: newNote.trim(),
                createdAt: new Date(),
            };

            const updatedNotes = [...notes, newNoteObj];
            await updateLead(lead.id, { notes: updatedNotes });

            setNotes(updatedNotes);
            setNewNote('');
            onLeadUpdate({ ...lead, notes: updatedNotes });
        } catch (error) {
            console.error('Error adding note:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteNoteClick = (noteId: string) => {
        setDeleteNoteId(noteId);
    };

    const handleConfirmDelete = async () => {
        if (!deleteNoteId) return;

        try {
            setIsSubmitting(true);
            const updatedNotes = notes.filter(note => note.id !== deleteNoteId);
            await updateLead(lead.id, { notes: updatedNotes });

            setNotes(updatedNotes);
            onLeadUpdate({ ...lead, notes: updatedNotes });
        } catch (error) {
            console.error('Error deleting note:', error);
        } finally {
            setIsSubmitting(false);
            setDeleteNoteId(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteNoteId(null);
    };

    const handleStatusChange = async (newStatus: string) => {
        try {
            setIsSubmitting(true);
            await updateLead(lead.id, { status: newStatus as UserSettableLeadStatus });
            onLeadUpdate({ ...lead, status: newStatus as UserSettableLeadStatus });
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (date: Date | undefined) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleEditNote = async (noteId: string, newText: string) => {
        try {
            setIsSubmitting(true);
            const updatedNotes = notes.map(note =>
                note.id === noteId
                    ? { ...note, text: newText }
                    : note
            );

            await updateLead(lead.id, { notes: updatedNotes });
            setNotes(updatedNotes);
            onLeadUpdate({ ...lead, notes: updatedNotes });
            setEditingNoteId(null);
            setShowUpdateSuccess(true);
        } catch (error) {
            console.error('Error updating note:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEditingNote = (note: Note) => {
        setEditingNoteId(note.id);
        setEditingNoteText(note.text);
    };

    const cancelEditingNote = () => {
        setEditingNoteId(null);
        setEditingNoteText('');
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                        {/* Business Information Card */}
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BusinessIcon /> Business Information
                                    </Typography>
                                    <Tooltip title="Copy Business ID">
                                        <IconButton onClick={handleCopyBusinessId} size="small">
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary">Company</Typography>
                                        <Typography variant="body1">{lead.company || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            <LocationOnIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                            Location
                                        </Typography>
                                        {editingLocation ? (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    value={cityValue}
                                                    onChange={(e) => setCityValue(e.target.value)}
                                                    placeholder="City"
                                                    sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    value={stateValue}
                                                    onChange={(e) => setStateValue(e.target.value)}
                                                    placeholder="State"
                                                    sx={{ flex: 1 }}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={async () => {
                                                        await handleUpdateField('city', cityValue);
                                                        await handleUpdateField('state', stateValue);
                                                        setEditingLocation(false);
                                                    }}
                                                    disabled={isSubmitting}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingLocation(false);
                                                        setCityValue(lead.city || '');
                                                        setStateValue(lead.state || '');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1">
                                                    {lead.city || 'N/A'}{lead.city && lead.state ? ', ' : ''}{lead.state || ''}
                                                </Typography>
                                                <IconButton size="small" onClick={() => setEditingLocation(true)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                            Phone
                                        </Typography>
                                        {editingPhone ? (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    value={phoneValue}
                                                    onChange={(e) => setPhoneValue(e.target.value)}
                                                    disabled={isSubmitting}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => handleUpdateField('phone', phoneValue)}
                                                    disabled={isSubmitting}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingPhone(false);
                                                        setPhoneValue(lead.phone || '');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1">{lead.phone || 'N/A'}</Typography>
                                                <IconButton size="small" onClick={() => setEditingPhone(true)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            <LanguageIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                            Website
                                        </Typography>
                                        {editingWebsite ? (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    value={websiteValue}
                                                    onChange={(e) => setWebsiteValue(e.target.value)}
                                                    disabled={isSubmitting}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => handleUpdateField('website', websiteValue)}
                                                    disabled={isSubmitting}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingWebsite(false);
                                                        setWebsiteValue(lead.website || '');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {lead.website ? (
                                                    <Link href={lead.website} target="_blank" rel="noopener noreferrer">
                                                        {lead.website}
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body1">No website</Typography>
                                                )}
                                                <IconButton size="small" onClick={() => setEditingWebsite(true)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Website Analysis Card */}
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AnalyticsIcon /> Website Analysis
                                </Typography>

                                {isLoadingBusiness ? (
                                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                                        Loading website analysis...
                                    </Typography>
                                ) : !business?.analysis_result?.detailed_results ? (
                                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                                        No website analysis data available
                                    </Typography>
                                ) : (
                                    <Stack spacing={2}>
                                        {/* Overall Scores */}
                                        <Box>
                                            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                                <Chip
                                                    label={`Score: ${business.analysis_result.score}/100`}
                                                    color={business.analysis_result.score >= 60 ? 'success' : 'error'}
                                                />
                                                <Chip
                                                    label={`Visual Score: ${business.analysis_result.visual_score}`}
                                                    color={business.analysis_result.visual_score >= -20 ? 'success' : 'error'}
                                                />
                                                {business.analysis_result.is_outdated && (
                                                    <Chip
                                                        label="Website Outdated"
                                                        color="error"
                                                        icon={<WarningIcon />}
                                                    />
                                                )}
                                            </Stack>
                                        </Box>

                                        {/* Detailed Results Accordion */}
                                        <Accordion>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography>Detailed Analysis Results</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    {/* Analytics */}
                                                    {business.analysis_result.detailed_results?.analytics && (
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <AnalyticsIcon color="primary" /> Analytics Tools
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ml: 4 }}>
                                                                {business.analysis_result.detailed_results.analytics.description}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Copyright */}
                                                    {business.analysis_result.detailed_results?.copyright && (
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CopyrightIcon color="primary" /> Copyright Information
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ml: 4 }}>
                                                                {business.analysis_result.detailed_results.copyright.description}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Domain */}
                                                    {business.analysis_result.detailed_results?.domain && (
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <DomainIcon color="primary" /> Domain Age
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ml: 4 }}>
                                                                {business.analysis_result.detailed_results.domain.description}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Mobile View */}
                                                    {business.analysis_result.detailed_results?.mobile_view && (
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <PhoneIcon color="primary" /> Mobile Responsiveness
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ml: 4 }}>
                                                                {business.analysis_result.detailed_results.mobile_view.description}
                                                            </Typography>
                                                            <Box sx={{ ml: 4, mt: 1 }}>
                                                                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                                                    <Chip
                                                                        size="small"
                                                                        label={business.analysis_result.detailed_results.mobile_view.viewport_meta ? "Viewport Meta: ✓" : "Viewport Meta: ✗"}
                                                                        color={business.analysis_result.detailed_results.mobile_view.viewport_meta ? "success" : "error"}
                                                                    />
                                                                    <Chip
                                                                        size="small"
                                                                        label={`Responsive Images: ${business.analysis_result.detailed_results.mobile_view.responsive_images}`}
                                                                        color={business.analysis_result.detailed_results.mobile_view.responsive_images > 0 ? "success" : "warning"}
                                                                    />
                                                                    <Chip
                                                                        size="small"
                                                                        label={`Touch Elements: ${business.analysis_result.detailed_results.mobile_view.touch_elements}`}
                                                                        color={business.analysis_result.detailed_results.mobile_view.touch_elements > 0 ? "success" : "warning"}
                                                                    />
                                                                </Stack>
                                                                {business.analysis_result.detailed_results.mobile_view.frameworks_detected.length > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                                        Mobile Frameworks: {business.analysis_result.detailed_results.mobile_view.frameworks_detected.join(', ')}
                                                                    </Typography>
                                                                )}
                                                                {business.analysis_result.detailed_results.mobile_view.media_queries.length > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                                        Media Queries: {business.analysis_result.detailed_results.mobile_view.media_queries.length} found
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Frameworks */}
                                                    {business.analysis_result.frameworks_detected && (
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CodeIcon color="primary" /> Frameworks Detected
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ml: 4 }}>
                                                                {business.analysis_result.frameworks_detected.join(', ')}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Missing Image Dimensions */}
                                                    {business.analysis_result.images_missing_dimensions > 0 && (
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <ImageIcon color="warning" /> Images Missing Dimensions
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ml: 4 }}>
                                                                {business.analysis_result.images_missing_dimensions} images found without dimensions
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Issues Accordion */}
                                        {business.analysis_result.issues && business.analysis_result.issues.length > 0 && (
                                            <Accordion>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BugReportIcon /> Issues Found ({business.analysis_result.issues.length})
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <List dense>
                                                        {business.analysis_result.issues.map((issue, index) => (
                                                            <ListItem key={index}>
                                                                <ListItemIcon>
                                                                    <ErrorIcon color="error" />
                                                                </ListItemIcon>
                                                                <ListItemText primary={issue} />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </AccordionDetails>
                                            </Accordion>
                                        )}
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* Lead Status Card */}
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Lead Status</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="status-select-label">Status</InputLabel>
                                            {isLockedStatus ? (
                                                <Box sx={{ mt: 2 }}>
                                                    <Chip
                                                        label={lead.status}
                                                        size="medium"
                                                        color={getStatusColor(lead.status)}
                                                        sx={{ fontWeight: 700, fontSize: '1rem', px: 3, minWidth: 120, height: 32, whiteSpace: 'nowrap' }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Select
                                                    labelId="status-select-label"
                                                    value={lead.status}
                                                    label="Status"
                                                    onChange={(e) => handleStatusChange(e.target.value)}
                                                    disabled={isSubmitting}
                                                >
                                                    {lead.status === 'new' && (
                                                        <MenuItem value="new" disabled>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip
                                                                    label="new"
                                                                    size="small"
                                                                    color={getStatusColor('new')}
                                                                />
                                                            </Box>
                                                        </MenuItem>
                                                    )}
                                                    {USER_SETTABLE_STATUSES.map((status) => (
                                                        <MenuItem key={status} value={status}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip
                                                                    label={status}
                                                                    size="small"
                                                                    color={getStatusColor(status)}
                                                                />
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </FormControl>
                                        {lead.status === 'new' && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Please review this lead and update its status.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="textSecondary">Assigned To</Typography>
                                        <Typography variant="body1">{lead.assignedTo || 'Unassigned'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                                        <Typography variant="body1">{formatDate(lead.createdAt)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                                        <Typography variant="body1">{formatDate(lead.updatedAt)}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>

                {/* Notes Section */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Notes</Typography>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    variant="outlined"
                                    placeholder="Add a note..."
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    disabled={isSubmitting}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            resize: 'vertical',
                                            minHeight: '4.5em',
                                            alignItems: 'flex-start',
                                            padding: '8px 14px'
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '0'
                                        }
                                    }}
                                />
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleAddNote}
                                        disabled={!newNote.trim() || isSubmitting}
                                    >
                                        Add Note
                                    </Button>
                                </Box>
                            </Box>
                            <Divider />
                            <List>
                                {notes.length === 0 ? (
                                    <ListItem>
                                        <ListItemText
                                            secondary="No notes yet. Add your first note above."
                                            sx={{ textAlign: 'center', fontStyle: 'italic' }}
                                        />
                                    </ListItem>
                                ) : (
                                    notes.map((note) => (
                                        <ListItem key={note.id}>
                                            {editingNoteId === note.id ? (
                                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        minRows={3}
                                                        value={editingNoteText}
                                                        onChange={(e) => setEditingNoteText(e.target.value)}
                                                        disabled={isSubmitting}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                resize: 'vertical',
                                                                minHeight: '4.5em',
                                                                alignItems: 'flex-start',
                                                                padding: '8px 14px'
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                padding: '0'
                                                            }
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleEditNote(note.id, editingNoteText)}
                                                            disabled={!editingNoteText.trim() || isSubmitting}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={cancelEditingNote}
                                                            disabled={isSubmitting}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <>
                                                    <ListItemText
                                                        primary={note.text}
                                                        secondary={formatDate(note.createdAt)}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="edit"
                                                            onClick={() => startEditingNote(note)}
                                                            disabled={isSubmitting}
                                                            sx={{ mr: 1 }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="delete"
                                                            onClick={() => handleDeleteNoteClick(note.id)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </>
                                            )}
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Snackbar
                open={showCopySuccess}
                autoHideDuration={3000}
                onClose={() => setShowCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Business ID copied to clipboard!
                </Alert>
            </Snackbar>

            <Snackbar
                open={showUpdateSuccess}
                autoHideDuration={3000}
                onClose={() => setShowUpdateSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowUpdateSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Information updated successfully!
                </Alert>
            </Snackbar>

            {/* Add Delete Confirmation Dialog */}
            <Dialog
                open={Boolean(deleteNoteId)}
                onClose={handleCancelDelete}
                aria-labelledby="delete-note-dialog-title"
            >
                <DialogTitle id="delete-note-dialog-title">
                    Delete Note
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this note? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        disabled={isSubmitting}
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Only show ManualOutreachForm if not locked status */}
            {!isLockedStatus && (
                <Box sx={{ mt: 3 }}>
                    <ManualOutreachForm lead={lead} onLeadUpdate={onLeadUpdate} />
                </Box>
            )}
        </Box>
    );
}; 